import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollSpyOptions {
    /** DOM IDs of the sections to observe, in display order. */
    sectionIds: string[];
    /** Scroll container element. Defaults to the viewport if not provided. */
    root?: HTMLElement | null;
    /** Top inset in pixels to offset for fixed headers. Default: 0 */
    topOffset?: number;
}

/**
 * Tracks which section is currently most visible within a scroll container
 * and provides a `scrollToSection` helper for sidebar click-to-jump.
 *
 * Uses `IntersectionObserver` for zero-cost passive tracking (no scroll
 * event listeners).
 */
export const useScrollSpy = ({ sectionIds, root = null, topOffset = 0 }: UseScrollSpyOptions) => {
    const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

    // Track each section's last-known intersection ratio so we can pick
    // the "most visible" even though the observer only reports *changed* entries.
    const ratioMap = useRef<Map<string, number>>(new Map());

    // Guard: suppress observer updates during programmatic scroll so the
    // sidebar doesn't flicker through intermediate sections.
    const isScrollingTo = useRef<string | null>(null);
    const scrollTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Track whether elements have been found in the DOM. If not, retry via
    // MutationObserver until they appear (handles the data-loading race).
    const observerRef = useRef<IntersectionObserver | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        if (sectionIds.length === 0 || !root) return;

        ratioMap.current = new Map(sectionIds.map((id) => [id, 0]));

        const lastSectionId = sectionIds[sectionIds.length - 1];

        const io = new IntersectionObserver(
            (entries) => {
                if (isScrollingTo.current) return;

                // If scrolled to the bottom, force the last section active
                const atBottom = root.scrollHeight - root.scrollTop - root.clientHeight < 2;
                if (atBottom) {
                    setActiveId(lastSectionId);
                    return;
                }

                for (const entry of entries) {
                    const id = entry.target.id;
                    if (ratioMap.current.has(id)) {
                        ratioMap.current.set(
                            id,
                            entry.isIntersecting ? entry.intersectionRatio : 0,
                        );
                    }
                }

                // Pick winner: highest ratio, ties broken by DOM order (first in sectionIds).
                let bestId: string | null = null;
                let bestRatio = 0;
                for (const id of sectionIds) {
                    const ratio = ratioMap.current.get(id) ?? 0;
                    if (ratio > bestRatio) {
                        bestRatio = ratio;
                        bestId = id;
                    }
                }

                if (bestId) {
                    setActiveId(bestId);
                }
            },
            {
                root,
                rootMargin: `-${topOffset}px 0px -40% 0px`,
                threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
            },
        );

        // Also listen for scroll events to catch the "at bottom" edge case
        // (IntersectionObserver alone won't fire if no intersection changes)
        const handleScroll = () => {
            if (isScrollingTo.current) return;
            const atBottom = root.scrollHeight - root.scrollTop - root.clientHeight < 2;
            if (atBottom) {
                setActiveId(lastSectionId);
            }
        };
        root.addEventListener('scroll', handleScroll, { passive: true });

        observerRef.current = io;

        // Attempt to observe all sections. Track which ones are missing.
        const observeExisting = () => {
            let allFound = true;
            for (const id of sectionIds) {
                const el = document.getElementById(id);
                if (el) {
                    io.observe(el);
                } else {
                    allFound = false;
                }
            }
            return allFound;
        };

        if (!observeExisting()) {
            // Some elements not in DOM yet — watch for them with MutationObserver
            const mo = new MutationObserver(() => {
                if (observeExisting()) {
                    mo.disconnect();
                }
            });
            mo.observe(root, { childList: true, subtree: true });
            mutationObserverRef.current = mo;
        }

        return () => {
            io.disconnect();
            root.removeEventListener('scroll', handleScroll);
            mutationObserverRef.current?.disconnect();
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionIds.join(','), root, topOffset]);

    const scrollToSection = useCallback((id: string) => {
        const el = document.getElementById(id);
        if (!el) return;

        // Lock the active state to the target immediately
        isScrollingTo.current = id;
        setActiveId(id);

        if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

        el.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Unlock after the smooth-scroll animation settles
        scrollTimeout.current = setTimeout(() => {
            isScrollingTo.current = null;
        }, 800);
    }, []);

    return { activeId, scrollToSection };
};
